/**
 * Hairstylist Analytics Helper
 * 
 * Provides functions to log analytics events for hairstylists
 * and track user interactions with hairstylist profiles.
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { DEFAULT_PROVIDER_TYPE } from '../config/providerTypes';

/**
 * Log a hairstylist analytics event to Firestore
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.hairstylistId - ID of the hairstylist
 * @param {string} params.eventType - Type of event (see HairstylistEventType)
 * @param {string|null} [params.userId] - ID of the user (null for anonymous)
 * @param {Object} [params.meta] - Additional metadata
 * @returns {Promise<string>} Document ID of the created event
 */
export async function logHairstylistEvent({ hairstylistId, eventType, userId = null, meta = {} }) {
  try {
    // Ne pas logguer les événements si l'utilisateur courant est l'admin
    // (les actions de l'admin ne doivent pas influencer les stats)
    try {
      // Lazy import pour éviter les cycles
      // eslint-disable-next-line global-require
      const { auth } = require('../firebase');
      const user = auth.currentUser;
      const displayName = user?.displayName || user?.email?.split('@')[0] || '';
      if (displayName.toLowerCase() === 'admin') {
        return null;
      }
    } catch (e) {
      // Si pour une raison quelconque l'import échoue, on continue normalement
    }

    // Validate required parameters
    if (!hairstylistId) {
      console.error('[logHairstylistEvent] Missing hairstylistId');
      return null;
    }

    if (!eventType) {
      console.error('[logHairstylistEvent] Missing eventType');
      return null;
    }

    // Create event document
    const eventData = {
      hairstylistId,
      event_type: eventType,
      event_date: serverTimestamp(),
      userId: userId || null,
      providerType: DEFAULT_PROVIDER_TYPE, // Type de prestataire (par défaut 'hair')
      meta: meta || {},
    };

    // Add to Firestore
    const docRef = await addDoc(collection(db, 'hairstylistStats'), eventData);

    // Log success in development
    if (process.env.NODE_ENV === 'development') {
    }

    return docRef.id;
  } catch (error) {
    console.error('[logHairstylistEvent] Error logging event:', error);
    return null;
  }
}

/**
 * Log when a hairstylist appears in search results
 */
export async function logSearchView(hairstylistId, userId = null, filters = []) {
  return logHairstylistEvent({
    hairstylistId,
    eventType: 'search_view',
    userId,
    meta: {
      source: 'search_page',
      search_filters: filters,
    },
  });
}

/**
 * Log when a user opens a hairstylist's profile page
 */
export async function logProfileOpen(hairstylistId, userId = null, source = 'search') {
  return logHairstylistEvent({
    hairstylistId,
    eventType: 'profile_open',
    userId,
    meta: {
      source,
    },
  });
}

/**
 * Log when a user opens the photo gallery
 */
export async function logGalleryOpen(hairstylistId, userId = null) {
  return logHairstylistEvent({
    hairstylistId,
    eventType: 'gallery_open',
    userId,
    meta: {
      source: 'profile_page',
    },
  });
}

/**
 * Log when a user clicks on a contact method
 */
export async function logContactClick(hairstylistId, contactType, userId = null) {
  const eventTypeMap = {
    instagram: 'contact_instagram_click',
    whatsapp: 'contact_whatsapp_click',
    tiktok: 'contact_tiktok_click',
    link: 'contact_link_click',
    externalLink: 'contact_link_click',
  };

  const eventType = eventTypeMap[contactType] || 'contact_link_click';

  return logHairstylistEvent({
    hairstylistId,
    eventType,
    userId,
    meta: {
      contact_type: contactType,
      source: 'profile_page',
    },
  });
}

/**
 * Log when a user adds a hairstylist to favorites
 */
export async function logFavoriteAdd(hairstylistId, userId) {
  return logHairstylistEvent({
    hairstylistId,
    eventType: 'favorite_add',
    userId,
    meta: {
      source: 'profile_page',
    },
  });
}

/**
 * Log when a user removes a hairstylist from favorites
 */
export async function logFavoriteRemove(hairstylistId, userId) {
  return logHairstylistEvent({
    hairstylistId,
    eventType: 'favorite_remove',
    userId,
    meta: {
      source: 'profile_page',
    },
  });
}

/**
 * Log when a user adds a review
 */
export async function logReviewAdded(hairstylistId, userId, rating) {
  return logHairstylistEvent({
    hairstylistId,
    eventType: 'review_added',
    userId,
    meta: {
      rating,
      source: 'profile_page',
    },
  });
}

/**
 * Log when a user updates their review
 */
export async function logReviewUpdated(hairstylistId, userId, rating) {
  return logHairstylistEvent({
    hairstylistId,
    eventType: 'review_updated',
    userId,
    meta: {
      rating,
      source: 'profile_page',
    },
  });
}

/**
 * Log when a user removes their review
 */
export async function logReviewRemoved(hairstylistId, userId) {
  return logHairstylistEvent({
    hairstylistId,
    eventType: 'review_removed',
    userId,
    meta: {
      source: 'profile_page',
    },
  });
}

